import {v4 as uuidv4} from 'uuid';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {buildControlFlowEdge, buildDataFlowEdge} from '../../buildStaticDataFromFlowchartConfigGraphQL';
import {populateMapFromStartSectionRows} from '../../mapFromStartUtils';
import {
  FlowchartStaticEdgeData,
  StaticTemplateFlowchart,
  TaskSchemaNodeSectionType,
} from '../../processFlowchartConfigEditorConstants';
import {convertTaskDefinitionVersionToSpec} from '../../processFlowchartConfigEditorNetworkUtils';
import {TaskSchemaStaticNodeData} from '../../processFlowchartConfigEditorNodeTypes';
import {createStaticNodeDataFromTaskDefinitionVersionSpec} from '../../processFlowchartConfigEditorUtils';
import {parseFieldPairKey} from '../../types/transition/transitionKeyUtils';
import {Transition} from '../../types/transition/TransitionTypes';
import {ConfigurableTaskDefinitionGQL} from '../../useAllSchemasById';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';

export interface BuildStaticFlowchartFromTransitionArgs {
  transition: Transition;
  ptdvIdToTaskSchemaId: Record<string, string>;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  rootNodeStaticData: TaskSchemaStaticNodeData;
}

class BuildStaticFlowchartFromTransitionRunner {
  private readonly transition: Transition;
  private readonly ptdvIdToTaskSchemaId: Record<string, string>;
  private readonly rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  private readonly rootNodeStaticData: TaskSchemaStaticNodeData;
  private readonly taskSchemaIdToDefinition: Map<string, ConfigurableTaskDefinitionGQL>;

  private nodesStaticData: Array<TaskSchemaStaticNodeData>;
  private edgesStaticData: Array<FlowchartStaticEdgeData>;
  private ptdvIdToNodeClientId: Record<string, string>;

  constructor(args: BuildStaticFlowchartFromTransitionArgs) {
    this.transition = args.transition;
    this.ptdvIdToTaskSchemaId = args.ptdvIdToTaskSchemaId;
    this.rootTaskSchema = args.rootTaskSchema;
    this.rootNodeStaticData = args.rootNodeStaticData;
    this.taskSchemaIdToDefinition = new Map(
      args.taskDefinitionsForSidepanel.map((def) => [def.latestVersion.processTaskSchema.id, def])
    );

    this.nodesStaticData = [];
    this.edgesStaticData = [];
    this.ptdvIdToNodeClientId = {};
  }

  run(): StaticTemplateFlowchart {
    this.buildNodes();
    this.buildControlFlowEdges();
    this.buildDataFlowEdges();
    this.populateMapFromStartRows();

    return {
      nodesStaticData: this.nodesStaticData,
      edgesStaticData: this.edgesStaticData,
    };
  }

  private buildNodes(): void {
    for (const sourcePTDVId of this.transition.sourcePTDVIds) {
      this.buildNodeForPTDV(sourcePTDVId, true);
    }
    this.buildNodeForPTDV(this.transition.targetPTDVId, false);
  }

  private buildNodeForPTDV(ptdvId: string, isSource: boolean): void {
    if (this.ptdvIdToNodeClientId[ptdvId] != null) {
      return;
    }

    const taskSchemaId = this.ptdvIdToTaskSchemaId[ptdvId];
    assert(taskSchemaId != null, `No task schema found for PTDV ${ptdvId}`);

    const definition = this.taskSchemaIdToDefinition.get(taskSchemaId);
    assert(definition != null, `No task definition found for task schema ${taskSchemaId}`);

    // Transitions are based on PTDV IDs, not node IDs
    // Create temporary node client ID for rendering within the transition modal
    // On save, we only pass back an updated transition with PTDV IDs
    const nodeClientId = uuidv4();
    this.ptdvIdToNodeClientId[ptdvId] = nodeClientId;

    const processTaskSchema = definition.latestVersion.processTaskSchema;
    const spec = {
      ...convertTaskDefinitionVersionToSpec(definition.latestVersion, processTaskSchema),
      clientId: ptdvId,
    };
    const nodeDetailsType = this.getNodeDetailsType(taskSchemaId, isSource);

    this.nodesStaticData.push({
      ...createStaticNodeDataFromTaskDefinitionVersionSpec({
        nodeClientId,
        taskDefinitionVersionSpec: spec,
        taskSchemaId,
        nodeDetailsType,
      }),
      isArchived: isArchived(processTaskSchema),
    });
  }

  private getNodeDetailsType(taskSchemaId: string, isSource: boolean): ProcessFlowchartNodeDetailsType {
    if (taskSchemaId === this.rootTaskSchema.id) {
      return isSource
        ? ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
        : ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
    }
    return ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS;
  }

  private buildControlFlowEdges(): void {
    const targetNodeId = this.ptdvIdToNodeClientId[this.transition.targetPTDVId];
    assert(targetNodeId != null, `No node found for target PTDV ${this.transition.targetPTDVId}`);

    for (const sourcePTDVId of this.transition.sourcePTDVIds) {
      const sourceNodeId = this.ptdvIdToNodeClientId[sourcePTDVId];
      assert(sourceNodeId != null, `No node found for source PTDV ${sourcePTDVId}`);
      this.edgesStaticData.push(buildControlFlowEdge({sourceNodeId, targetNodeId}));
    }
  }

  private buildDataFlowEdges(): void {
    for (const fieldPairKey of Object.keys(this.transition.customMappingDataByFieldPair)) {
      const parsed = parseFieldPairKey(fieldPairKey);
      assert(parsed != null, `Could not parse field pair key: ${fieldPairKey}`);

      const sourceNodeId = this.ptdvIdToNodeClientId[parsed.sourcePTDVId];
      const targetNodeId = this.ptdvIdToNodeClientId[parsed.targetPTDVId];
      assert(
        sourceNodeId != null && targetNodeId != null,
        `Missing node for data flow edge: source=${parsed.sourcePTDVId} target=${parsed.targetPTDVId}`
      );

      this.edgesStaticData.push(
        buildDataFlowEdge({
          sourceNodeId,
          targetNodeId,
          fromFieldId: parsed.sourceFieldClientId,
          fromFieldSectionType: parsed.sourceSectionType,
          toFieldId: parsed.targetFieldClientId,
          toFieldSectionType: parsed.targetSectionType,
        })
      );
    }
  }

  /**
   * Populates MAP_FROM_START section rows on non-root nodes
   */
  private populateMapFromStartRows(): void {
    // Collect Map From Start field IDs grouped by source node ID
    const mapFromStartFieldIdsByNodeId: Record<string, Array<string>> = {};
    for (const fieldPairKey of Object.keys(this.transition.customMappingDataByFieldPair)) {
      const parsed = parseFieldPairKey(fieldPairKey);
      if (parsed == null || parsed.sourceSectionType !== TaskSchemaNodeSectionType.MAP_FROM_START) {
        continue;
      }
      const sourceNodeId = this.ptdvIdToNodeClientId[parsed.sourcePTDVId];
      assert(sourceNodeId != null, `No node found for source PTDV ${parsed.sourcePTDVId}`);
      (mapFromStartFieldIdsByNodeId[sourceNodeId] ??= []).push(parsed.sourceFieldClientId);
    }

    populateMapFromStartSectionRows(
      this.nodesStaticData,
      this.rootNodeStaticData,
      mapFromStartFieldIdsByNodeId
    );
  }
}

export default function buildStaticFlowchartFromTransition(
  args: BuildStaticFlowchartFromTransitionArgs
): StaticTemplateFlowchart {
  const runner = new BuildStaticFlowchartFromTransitionRunner(args);
  return runner.run();
}

export function buildRootNodeStaticData(
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL,
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>
): TaskSchemaStaticNodeData {
  const definition = taskDefinitionsForSidepanel.find(
    (def) => def.latestVersion.processTaskSchema.id === rootTaskSchema.id
  );
  assert(definition != null, `No task definition found for root task schema ${rootTaskSchema.id}`);

  const processTaskSchema = definition.latestVersion.processTaskSchema;
  const spec = convertTaskDefinitionVersionToSpec(definition.latestVersion, processTaskSchema);

  return createStaticNodeDataFromTaskDefinitionVersionSpec({
    nodeClientId: uuidv4(),
    taskDefinitionVersionSpec: spec,
    taskSchemaId: rootTaskSchema.id,
    nodeDetailsType: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
  });
}
